import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import SEOHead from '../components/SEOHead';

export default function WebsiteDevelopmentPage() {
  const router = useRouter();
  
  // English-only page - canonical URL is fixed
  const canonicalUrl = '/website-development';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Website Development',
    description: 'Professional website development services in Lithuania. Custom websites built with Next.js, React, and modern technologies for businesses seeking online presence.',
    provider: {
      '@type': 'Organization',
      name: 'LB Visible',
      url: 'https://www.lbvisible.com',
      knowsAbout: [
        'Website Development',
        'Web Design',
        'Next.js Development',
        'React Development',
        'Custom Websites',
      ],
    },
    areaServed: {
      '@type': 'Country',
      name: 'Lithuania',
    },
  };

  return (
    <>
      <SEOHead
        title='Website Development | LBVisible'
        description='Professional website development services in Lithuania. Custom websites built with Next.js, React, and modern technologies for businesses seeking online presence.'
        keywords='website development, web development Lithuania, custom websites, Next.js development, React development, professional web design'
        canonicalUrl={canonicalUrl}
        lang='en'
        structuredData={structuredData}
      />

      <main className='min-h-screen max-w-4xl mx-auto px-4 sm:px-6 py-12'>
        <article className='prose prose-lg dark:prose-invert max-w-none'>
          <style jsx global>{`
            article h1 {
              font-size: 2.25rem;
              font-weight: 700;
              margin-top: 2rem;
              margin-bottom: 1.5rem;
              line-height: 1.2;
            }
            article h2 {
              font-size: 1.875rem;
              font-weight: 700;
              margin-top: 2rem;
              margin-bottom: 1rem;
              line-height: 1.3;
            }
            article h3 {
              font-size: 1.5rem;
              font-weight: 600;
              margin-top: 1.5rem;
              margin-bottom: 0.75rem;
              line-height: 1.4;
            }
            article p {
              margin-bottom: 1.5rem;
              line-height: 1.75;
            }
          `}</style>
          <h1 className='text-gray-900 dark:text-white'>
            Website Development and Programming in Lithuania
          </h1>
          
          <h2 className='text-gray-900 dark:text-white'>
            Why Professional Website Development Matters for Your Business
          </h2>
          <p className='text-gray-700 dark:text-gray-400'>
            In today&apos;s digital landscape, having a professional website is not just an option—it&apos;s a necessity. Website development goes beyond creating a simple online presence. It&apos;s about building a digital foundation that drives business growth, engages customers, and converts visitors into clients. Professional website development ensures your site is fast, secure, mobile-responsive, and optimized for search engines from day one.
          </p>

          <h2 className='text-gray-900 dark:text-white'>
            Our Website Development Process
          </h2>
          <p className='text-gray-700 dark:text-gray-400'>
            At LBVisible, we follow a comprehensive website development process designed to deliver results. We start by understanding your business goals, target audience, and unique requirements. Our team then creates a custom solution using modern technologies like Next.js and React, ensuring your website is fast, scalable, and future-proof. We focus on user experience, performance optimization, and SEO best practices throughout the development process.
          </p>

          <h3 className='text-gray-900 dark:text-white'>
            React and Next.js Programming
          </h3>
          <p className='text-gray-700 dark:text-gray-400'>
            We specialize in React and Next.js programming, leveraging these powerful frameworks to create dynamic, high-performance websites. Next.js provides server-side rendering capabilities, ensuring your content loads quickly and ranks well in search engines. Our React development expertise allows us to build interactive user interfaces that engage visitors and provide exceptional user experiences.
          </p>

          <h3 className='text-gray-900 dark:text-white'>
            SEO Optimized Websites
          </h3>
          <p className='text-gray-700 dark:text-gray-400'>
            Every website we develop is built with SEO in mind. We implement proper meta tags, structured data, semantic HTML, and optimize page speed to ensure your site ranks well in Google search results. Our SEO-optimized websites help you attract organic traffic and reach your target audience effectively.
          </p>

          <h2 className='text-gray-900 dark:text-white'>
            Why Choose LBVisible for Website Development
          </h2>
          <p className='text-gray-700 dark:text-gray-400'>
            Choosing the right website development partner is crucial for your business success. At LBVisible, we combine technical expertise with business understanding to deliver websites that not only look great but also drive results. We use modern technologies, follow best practices, and provide ongoing support to ensure your website continues to perform well long after launch.
          </p>

          <p className='text-gray-700 dark:text-gray-400'>
            Our website development services include custom design, responsive development, CMS integration, SEO optimization, and ongoing maintenance. We work closely with you throughout the process, ensuring your vision becomes reality and your business goals are achieved.
          </p>

          <p className='text-gray-700 dark:text-gray-400'>
            Whether you&apos;re a startup looking to establish your online presence or an established business seeking to upgrade your website, we have the expertise and experience to help you succeed. Contact us today to discuss your website development needs and discover how we can help your business grow online.
          </p>
        </article>
      </main>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async () => {
  return {
    props: {},
  };
};

